/**
 * A person's mobile number, as they typed it, in E.164 (slice 2e spec §3). A
 * number with no country code takes the workspace timezone's country, from a
 * small map of the zones people are likely in; a zone not in it needs the
 * `+`. No phone-number library: only the shape is checked here, and Apple
 * says whether the number is real when the code arrives.
 */

/**
 * A country: its two letters (the phone field's label), its calling code;
 * whether its numbers keep the leading 0 (Italy); and whether a number
 * written the national way can't be made E.164 here (`needsPlus`).
 */
export type Country = { iso: string; code: string; keepsZero?: boolean; needsPlus?: boolean };

const C = {
  US: { iso: "US", code: "1" },
  CA: { iso: "CA", code: "1" },
  GB: { iso: "GB", code: "44" },
  IE: { iso: "IE", code: "353" },
  AU: { iso: "AU", code: "61" },
  NZ: { iso: "NZ", code: "64" },
  IN: { iso: "IN", code: "91" },
  DE: { iso: "DE", code: "49" },
  FR: { iso: "FR", code: "33" },
  ES: { iso: "ES", code: "34" },
  IT: { iso: "IT", code: "39", keepsZero: true },
  NL: { iso: "NL", code: "31" },
  BE: { iso: "BE", code: "32" },
  CH: { iso: "CH", code: "41" },
  AT: { iso: "AT", code: "43" },
  SE: { iso: "SE", code: "46" },
  NO: { iso: "NO", code: "47" },
  DK: { iso: "DK", code: "45" },
  FI: { iso: "FI", code: "358" },
  PT: { iso: "PT", code: "351" },
  PL: { iso: "PL", code: "48" },
  GR: { iso: "GR", code: "30" },
  IL: { iso: "IL", code: "972" },
  AE: { iso: "AE", code: "971" },
  ZA: { iso: "ZA", code: "27" },
  NG: { iso: "NG", code: "234" },
  KE: { iso: "KE", code: "254" },
  JP: { iso: "JP", code: "81" },
  KR: { iso: "KR", code: "82" },
  SG: { iso: "SG", code: "65" },
  HK: { iso: "HK", code: "852" },
  PH: { iso: "PH", code: "63" },
  // Mobiles here can't be made E.164 by dropping a trunk 0, and this file
  // won't grow per-country rules: a national number is refused, and the
  // person adds the +. Argentina's mobiles drop the 0 and the 15 and gain a
  // 9 (`(011) 15 2345-6789` is +54 9 11 2345 6789); Mexico's old 044, 045
  // and 1 prefixes still turn up.
  MX: { iso: "MX", code: "52", needsPlus: true },
  BR: { iso: "BR", code: "55" },
  AR: { iso: "AR", code: "54", needsPlus: true },
  CO: { iso: "CO", code: "57" },
  CL: { iso: "CL", code: "56" },
} satisfies Record<string, Country>;

/** IANA zones → their country. */
const ZONES: Record<string, Country> = {
  ...Object.fromEntries(
    [
      "America/New_York",
      "America/Detroit",
      "America/Chicago",
      "America/Denver",
      "America/Boise",
      "America/Phoenix",
      "America/Los_Angeles",
      "America/Anchorage",
      "America/Juneau",
      "America/Indiana/Indianapolis",
      "America/Kentucky/Louisville",
      "Pacific/Honolulu",
      "America/Puerto_Rico",
      "America/Jamaica",
      "America/Nassau",
      "America/Barbados",
      "America/Port_of_Spain",
      "America/Santo_Domingo",
      "Atlantic/Bermuda",
    ].map((zone) => [zone, C.US]),
  ),
  ...Object.fromEntries(
    [
      "America/Toronto",
      "America/Montreal",
      "America/Vancouver",
      "America/Edmonton",
      "America/Winnipeg",
      "America/Regina",
      "America/Halifax",
      "America/St_Johns",
      "America/Moncton",
      "America/Whitehorse",
      "America/Yellowknife",
      "America/Iqaluit",
    ].map((zone) => [zone, C.CA]),
  ),
  "Europe/London": C.GB,
  "Europe/Belfast": C.GB,
  "Europe/Dublin": C.IE,
  "Australia/Sydney": C.AU,
  "Australia/Melbourne": C.AU,
  "Australia/Brisbane": C.AU,
  "Australia/Perth": C.AU,
  "Australia/Adelaide": C.AU,
  "Australia/Hobart": C.AU,
  "Australia/Darwin": C.AU,
  "Pacific/Auckland": C.NZ,
  "Asia/Kolkata": C.IN,
  "Asia/Calcutta": C.IN,
  "Europe/Berlin": C.DE,
  "Europe/Paris": C.FR,
  "Europe/Madrid": C.ES,
  "Atlantic/Canary": C.ES,
  "Europe/Rome": C.IT,
  "Europe/Amsterdam": C.NL,
  "Europe/Brussels": C.BE,
  "Europe/Zurich": C.CH,
  "Europe/Vienna": C.AT,
  "Europe/Stockholm": C.SE,
  "Europe/Oslo": C.NO,
  "Europe/Copenhagen": C.DK,
  "Europe/Helsinki": C.FI,
  "Europe/Lisbon": C.PT,
  "Europe/Warsaw": C.PL,
  "Europe/Athens": C.GR,
  "Asia/Jerusalem": C.IL,
  "Asia/Tel_Aviv": C.IL,
  "Asia/Dubai": C.AE,
  "Africa/Johannesburg": C.ZA,
  "Africa/Lagos": C.NG,
  "Africa/Nairobi": C.KE,
  "Asia/Tokyo": C.JP,
  "Asia/Seoul": C.KR,
  "Asia/Singapore": C.SG,
  "Asia/Hong_Kong": C.HK,
  "Asia/Manila": C.PH,
  "America/Mexico_City": C.MX,
  "America/Monterrey": C.MX,
  "America/Tijuana": C.MX,
  "America/Cancun": C.MX,
  "America/Sao_Paulo": C.BR,
  "America/Argentina/Buenos_Aires": C.AR,
  "America/Buenos_Aires": C.AR,
  "America/Bogota": C.CO,
  "America/Santiago": C.CL,
};

/** Every country the phone field offers, by its two letters. */
export const COUNTRIES: Country[] = Object.values(C).sort((a, b) => a.iso.localeCompare(b.iso));

/** The timezone's country, or null in a zone not in the map. */
export function countryOfZone(timezone: string | null | undefined): Country | null {
  return (timezone && ZONES[timezone]) || null;
}

const E164 = /^\+[1-9]\d{6,14}$/;

/**
 * The number in E.164, or null when it can't be one. `+` or `00` starts an
 * international number; anything else is national, in `timezone`'s country.
 */
export function e164(input: string, timezone?: string | null): string | null {
  return e164In(input, countryOfZone(timezone));
}

/** `e164`, a national number taken in `country` (the phone field's choice). */
export function e164In(input: string, country: Country | null): string | null {
  const trimmed = input.trim();
  if (!trimmed || /[^\d\s()+.\-/]/.test(trimmed)) return null;
  const digits = trimmed.replace(/\(0\)/, "").replace(/\D/g, "");
  let full: string | null;
  if (trimmed.startsWith("+")) full = digits;
  else if (digits.startsWith("00")) full = digits.slice(2);
  else full = national(digits, country);
  // NANP numbers are always eleven digits.
  const nanpOk = !full?.startsWith("1") || full.length === 11;
  return full && nanpOk && E164.test(`+${full}`) ? `+${full}` : null;
}

/**
 * A national number with its country's code, the trunk 0 dropped (but
 * Italy's); null with no country, or a country whose numbers need the +.
 */
function national(digits: string, country: Country | null): string | null {
  if (!country || country.needsPlus) return null;
  if (country.code === "1") return nanp(digits);
  const trunk = !country.keepsZero && digits.startsWith("0");
  return country.code + (trunk ? digits.slice(1) : digits);
}

/** NANP: ten digits, perhaps after the 1. */
function nanp(digits: string): string | null {
  const ten = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return ten.length === 10 ? `1${ten}` : null;
}
